import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Eye, Copy, Trash } from '@phosphor-icons/react';

const AzureManageUrls = () => {
  const [deployments, setDeployments] = useState([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`/api/azure/deployments`, { withCredentials: true });
      setDeployments(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure? This will permanently delete the Azure Resource Group, including the Storage Account and CDN endpoint.")) return;
    try {
      await axios.delete(`/api/azure/deployments/${id}`, { withCredentials: true });
      toast.success("Azure Resource Group deletion initiated in background.");
      fetchHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete deployment");
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black mb-2 text-[#0078D4]">MANAGE URLs</h1>
        <p className="text-slate-400 font-mono">View, manage, and delete your deployed Azure static sites.</p>
      </div>
      
      <div className="border border-white/10 bg-card overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 bg-black/20">
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Date</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Site Name</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Prefix</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Status</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider">URL</th>
              <th className="p-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {deployments.map(dep => (
              <tr key={dep.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="p-4 text-sm text-slate-400 font-mono">{format(new Date(dep.created_at), 'yyyy-MM-dd HH:mm')}</td>
                <td className="p-4 font-mono text-sm font-bold text-white">{dep.site_name}</td>
                <td className="p-4 font-mono text-sm text-slate-400">{dep.prefix}</td>
                <td className="p-4 text-sm">
                  <span className={`px-2 py-1 text-xs font-bold tracking-wider ${
                    dep.status === 'live' ? 'bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/20' : 
                    dep.status === 'failed' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 
                    'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20'
                  }`}>
                    {dep.status}
                  </span>
                </td>
                <td className="p-4 text-sm">
                  {dep.public_url ? (() => {
                    const finalUrl = dep.public_url + '/index.html';
                    return (
                      <div className="flex items-center gap-2">
                        <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-slate-400 font-mono truncate w-48 select-all" title={finalUrl}>
                          {finalUrl}
                        </div>
                        <button 
                          onClick={() => { navigator.clipboard.writeText(finalUrl); toast.success("Copied!"); }}
                          className="text-slate-400 hover:text-white transition-colors p-1"
                        >
                          <Copy size={16} />
                        </button>
                        <a href={finalUrl} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#0078D4] transition-colors p-1">
                          <Eye size={16} />
                        </a>
                      </div>
                    );
                  })() : '-'}
                </td>
                <td className="p-4 flex items-center justify-end gap-2">
                  <button 
                    onClick={() => handleDelete(dep.id)} 
                    className="bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white transition-colors px-3 py-1 font-bold text-xs flex items-center gap-1 uppercase tracking-wider"
                    title="Delete Resource Group"
                  >
                    <Trash size={14} /> Delete
                  </button>
                </td>
              </tr>
            ))}
            {deployments.length === 0 && (
              <tr>
                <td colSpan="6" className="p-8 text-center text-slate-500">No Azure deployments found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AzureManageUrls;